// 2026-10-05 · Órdenes remotas del admin a este dispositivo (`device.command`).
//
// Sobre: { commandId, type, args, requestedBy, requestedAt, target }.
//  - Solo las de ESTE dispositivo (`target` = sessionIdentifier); el resto se ignoran.
//  - Una orden se ejecuta una vez: las ya vistas se guardan en localStorage
//    (el tiempo real puede reenviarla al reconectar o tras recargar).
//  - Caducan a los 15 min (un TPV apagado no limpia la caché horas después).
//  - Se despachan por `type` a su handler; un tipo desconocido se contesta
//    `failed` «unsupported» (la fase 2 añadirá tipos sin tocar esto).
//  - Nunca en mitad de un cobro: si hay uno abierto se contesta `received`
//    («aplazada») y se ejecuta cuando se cierra.
//
// El handler recibe (args, ctx) y es responsable de contestar `done` ANTES de
// recargar (ctx.sendResult); si lanza, se contesta `failed`.

export const HANDLED_KEY = "tmo:deviceCommands:handled";
export const MAX_AGE_MS = 15 * 60 * 1000;
const MAX_HANDLED = 50;

function readHandled(storage) {
  try {
    const v = JSON.parse(storage.getItem(HANDLED_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function writeHandled(storage, list) {
  try {
    storage.setItem(HANDLED_KEY, JSON.stringify(list.slice(-MAX_HANDLED)));
  } catch {
    /* modo privado: queda la memoria */
  }
}

export function createDeviceCommandRunner({
  getLocalSid,
  isPaymentOpen = () => false,
  sendResult,
  handlers,
  storage = window.localStorage,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
  pollMs = 2000,
}) {
  const seen = new Set(readHandled(storage));
  const timers = new Set();
  let disposed = false;

  const markHandled = (id) => {
    seen.add(id);
    writeHandled(storage, [...readHandled(storage).filter((x) => x !== id), id]);
  };

  const reply = (id, body) => {
    try {
      return Promise.resolve(sendResult(id, body)).catch(() => undefined);
    } catch {
      return Promise.resolve();
    }
  };

  const run = async (env) => {
    const ctx = { commandId: env.commandId, sendResult: (body) => reply(env.commandId, body) };
    try {
      await reply(env.commandId, { status: "running" });
      await handlers[env.type](env.args || {}, ctx);
    } catch (e) {
      await reply(env.commandId, { status: "failed", message: String((e && e.message) || e).slice(0, 400) });
    }
  };

  const waitForPayment = (env) => {
    const tick = () => {
      if (disposed) return;
      if (isPaymentOpen()) {
        const t = setTimer(tick, pollMs);
        timers.add(t);
        return;
      }
      run(env);
    };
    const t = setTimer(tick, pollMs);
    timers.add(t);
  };

  return {
    /** Devuelve qué hizo (para tests y logs). */
    handle(env) {
      if (disposed || !env || !env.commandId || !env.type) return "ignored";
      const localSid = getLocalSid();
      if (!localSid || env.target !== localSid) return "notMine";
      if (seen.has(env.commandId)) return "duplicate";
      markHandled(env.commandId);

      const at = env.requestedAt ? Date.parse(env.requestedAt) : NaN;
      if (!Number.isNaN(at) && now() - at > MAX_AGE_MS) {
        reply(env.commandId, { status: "failed", message: "expired" });
        return "expired";
      }
      if (!handlers[env.type]) {
        reply(env.commandId, { status: "failed", message: "unsupported" });
        return "unsupported";
      }
      if (isPaymentOpen()) {
        reply(env.commandId, { status: "received", message: "Aplazada: hay un cobro abierto" });
        waitForPayment(env);
        return "deferred";
      }
      run(env);
      return "running";
    },
    dispose() {
      disposed = true;
      timers.forEach((t) => clearTimer(t));
      timers.clear();
    },
  };
}
